import { createGLightbox, openInlineHtml } from './lib/glightbox';
import { create, registerPlugin } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { FF_utils } from './lib/utils.js';

/** Must match Lambda formSubmit — Zendesk cannot use the instance support address as requester. */
const BLOCKED_SERVICE_REQUEST_EMAIL = 'support@freeflysystems.com';

class FF_serviceRequest {
	submissionId: string;
	ticket: string | null = null;
	agent: string | null = null;
	isCheckup = false;
	product: string | null = null;
	sku: string | null = null;
	DOM = {} as Record<string, any>;
	orderPond: unknown;
	issuePond: unknown;

	state = {
		working: false,
		step: 1,
		valid: false,
		source: null,
		family: null,
		product: null,
		estimate: true,
		error: null,
		success: null,
		raNumber: null,
		emailIsValid: null,
		serialIsValid: null,
		purchaseDateIsValid: null,
	};

	constructor() {
		this.setSubmissionId();
		this.setQueryParams();
		this.cache();
		this.events();
		this.prepopulate();
		this.initUpload();
		this.validate();
		this.render();
	}

	cache() {
		const form = document.querySelector('.js-serviceRequestForm');
		this.DOM = {
			form,
			section1: form.querySelector('.js-section1'),
			section2: form.querySelector('.js-section2'),
			section3: form.querySelector('.js-section3'),
			section4: form.querySelector('.js-section4'),
			section5: form.querySelector('.js-section5'),
			advanceButton: form.querySelector('.js-advanceButton'),
			submitButton: form.querySelector('.js-submitButton'),
			spinner: form.querySelector('.js-spinner'),

			allInputs: form.querySelectorAll('input, select, textarea'),

			fName: form.querySelector('input[name=firstName]'),
			lName: form.querySelector('input[name=lastName]'),
			email: form.querySelector('input[name=email]'),

			source: form.querySelector('select[name=purchaseSource]'),
			purchaseDate: form.querySelector('input[name=purchaseDate]'),
			dealerNameField: form.querySelector('.js-dealerNameField'),
			orderNumberField: form.querySelector('.js-orderNumberField'),
			orderFiles: form.querySelector('input[name="orderFile[]"]'),

			productFamily: form.querySelector('select[name=productFamily]'),
			productSelect: form.querySelector('select[name=productSelect]'),
			// productText: form.querySelector("input[name=productText]"),
			productSelectWrapper: form.querySelector('.js-productSelect'),
			// productTextWrapper: form.querySelector(".js-productText"),
			serialNumberRow: form.querySelector('.js-serialNumberRow'),
			serialNumber: form.querySelector('input[name=serial]'),
			serialInfoTrigger: form.querySelector('.js-serialInfoTrigger'),
			otherProductRow: form.querySelector('.js-otherProductRow'),
			otherProduct: form.querySelector('input[name=otherProduct]'),

			nature: form.querySelector('select[name=nature]'),
			cameraRow: form.querySelector('.js-cameraRow'),
			cameraRadios: form.querySelectorAll('input[name=camera]'),
			description: form.querySelector('textarea[name=description]'),
			attempt: form.querySelector('textarea[name=attempt]'),
			issueFiles: form.querySelector('input[name="issueFile[]"]'),

			estimateRadios: form.querySelectorAll('input[name=estimate]'),
			preauthorizeRow: form.querySelector('.js-preauthorizeRow'),

			infoTriggers: form.querySelectorAll('.js-infoTrigger'),

			warrantyNo: document.querySelector('#service-request-warranty-no'),
		};
	}

	events() {
		this.DOM.form.addEventListener('submit', this.formSubmit.bind(this));
		this.DOM.form.addEventListener('keyup', this.formKeyUp.bind(this));
		this.DOM.advanceButton.addEventListener('click', this.advanceClick.bind(this));
		this.DOM.source.addEventListener('input', this.sourceInput.bind(this));
		for (let i = 0; i < this.DOM.allInputs.length; i++) {
			this.DOM.allInputs[i].addEventListener('input', this.globalInput.bind(this));
		}
		this.DOM.productFamily.addEventListener('input', this.familyInput.bind(this));
		this.DOM.productSelect.addEventListener('input', this.productSelectInput.bind(this));
		createGLightbox({
			selector: '.js-serviceRequestForm .js-infoTrigger',
			skin: 'ff-serviceRequestModal',
			width: 'auto',
			height: 'auto',
			draggable: false,
			touchNavigation: false,
		});
		for (let i = 0; i < this.DOM.estimateRadios.length; i++) {
			this.DOM.estimateRadios[i].addEventListener('change', this.estimateChange.bind(this));
		}
		this.DOM.serialInfoTrigger.addEventListener('click', this.serialInfoTriggerClick.bind(this));
	}

	setSubmissionId() {
		const ff_utils = new FF_utils();
		this.submissionId = ff_utils.cuid();
	}

	setQueryParams() {
		const urlParams = new URLSearchParams(window.location.search);
		this.ticket = urlParams.get('ticket');
		this.agent = urlParams.get('agent');
		this.isCheckup = urlParams.get('checkup') === '1';
		this.product = urlParams.get('product');
		this.sku = urlParams.get('sku');
	}

	selectSecretIssue(value, label) {
		var opt = document.createElement('option');
		opt.value = value;
		opt.innerHTML = label;
		this.DOM.nature.appendChild(opt);
		this.DOM.nature.value = value;
	}

	prepopulate() {
		if (this.isCheckup) {
			this.DOM.nature.value = 'PREVENTIVE';
		}
		if (this.sku) {
			if (this.sku.toUpperCase() === 'ASTRO-PILOT-PRO-UPGRADE') {
				this.product = 'ASTRO';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Pilot Pro integration for Astro';
				this.DOM.attempt.value = 'Not required';
			} else if (this.sku.toUpperCase() === 'PILOT-PRO-RFD-DIU-INTEGRATION-ALTA-X') {
				this.product = 'ALTA_X';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Pilot Pro DIU Integration for Alta X';
				this.DOM.attempt.value = 'Not required';
				this.DOM.warrantyNo.checked = true;
			} else if (this.sku.toUpperCase() === 'UPGRADE-ALTAX-PP-RFD') {
				this.product = 'ALTA_X';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Pilot Pro + RFD integration for Alta X';
				this.DOM.attempt.value = 'Not required';
				this.DOM.warrantyNo.checked = true;
			} else if (this.sku.toUpperCase() === 'UPGRADE-CARBON') {
				this.product = 'MOVI_CARBON';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Payload upgrade for Movi Carbon';
				this.DOM.attempt.value = 'Not required';
				this.DOM.cameraRow.style.display = 'block';
			} else if (this.sku.toUpperCase() === 'EMBER-S5K-EF-UPGRADE') {
				this.product = 'EMBER';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Ember S5K EF Mount Upgrade';
				this.DOM.attempt.value = 'Not required';
			} else if (this.sku.toUpperCase() === 'EMBER-S5K-L-MOUNT-UPGRADE') {
				this.product = 'EMBER';
				this.selectSecretIssue('UPGRADE', 'Upgrade');
				this.DOM.description.value = 'Ember S5K L-Mount Upgrade';
				this.DOM.attempt.value = 'Not required';
			} else if (this.sku.toUpperCase() === 'FLUX-RECALIBRATION') {
				this.product = 'FLUX';
				this.selectSecretIssue('RECALIBRATION', 'Recalibration');
				this.DOM.description.value = 'Flux LiDAR recalibration';
				this.DOM.attempt.value = 'Not required';
			}
		}

		if (this.product) {
			let family, product;
			for (const [key, value] of Object.entries(this.productOptions)) {
				const found = value.find((el) => el.value === this.product.toUpperCase());
				if (found) {
					family = key;
					product = found.value;
					break;
				}
			}
			if (family) {
				this.DOM.productFamily.value = family;
				this.DOM.productFamily.dispatchEvent(new Event('input', { bubbles: true }));
			}
			if (product) {
				this.DOM.productSelect.value = product;
				this.DOM.productSelect.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}
	}

	formSubmit(e) {
		e.preventDefault();
		if (this.state.step !== 5 || !this.validate()) return;
		const emailTrimmed = (this.DOM.email.value || '').trim().toLowerCase();
		if (emailTrimmed === BLOCKED_SERVICE_REQUEST_EMAIL) {
			this.state.emailIsValid = false;
			this.validate();
			this.render();
			window.scrollTo({
				top: this.DOM.email.getBoundingClientRect().top + window.scrollY - 80,
				behavior: 'smooth',
			});
			return;
		}
		if (this.state.working) return;
		this.state.working = true;
		this.render();
		const formData = new FormData(this.DOM.form);
		formData.append('action', 'service_submit');
		formData.append('id', this.submissionId);
		if (this.ticket) {
			formData.append('ticket', this.ticket);
		}
		if (this.agent) {
			formData.append('agent', this.agent);
		}
		if (this.isCheckup) {
			formData.append('isCheckup', 'true');
		}

		grecaptcha.ready(() => {
			grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then((token) => {
				formData.append('g-recaptcha-response', token);
				try {
					fetch(ajaxurl, {
						method: 'POST',
						body: formData,
					}).then(this.formResponse.bind(this));
				} catch (error) {
					console.log(error);
					this.showErrorMessage(error);
					window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });
					this.state.working = false;
					this.render();
				}
			});
		});
	}

	async formResponse(res) {
		this.state.working = false;
		let data: { ra?: string; error?: string } = {};
		try {
			data = await res.json();
		} catch {
			this.showErrorMessage(`Request could not be processed.`);
			this.render();
			return;
		}
		window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });
		if (data.ra) {
			this.showSuccessMessage(data.ra);
			this.state.success = true;
		} else {
			const msg =
				typeof data.error === 'string' && data.error
					? data.error
					: `Request could not be processed.`;
			this.showErrorMessage(msg);
		}
		this.render();
	}

	formKeyUp() {
		this.validate();
		this.render();
	}

	globalInput() {
		this.validate();
		this.render();
	}

	advanceClick() {
		this.validate();
		if (!this.state.valid) return;
		this.state.step = this.state.step + 1;
		this.validate();
		this.render();
		this.scrollToStep();
	}

	render() {
		this.renderSteps();
		this.renderFooter();
		this.renderConditionalFields();
		this.renderErrors();
	}

	renderSteps() {
		if (this.state.step > 1) {
			this.DOM.section2.classList.remove('js-hide');
		} else {
			this.DOM.section2.classList.add('js-hide');
		}
		if (this.state.step > 2) {
			this.DOM.section3.classList.remove('js-hide');
		} else {
			this.DOM.section3.classList.add('js-hide');
		}
		if (this.state.step > 3) {
			this.DOM.section4.classList.remove('js-hide');
		} else {
			this.DOM.section4.classList.add('js-hide');
		}
		if (this.state.step > 4) {
			this.DOM.section5.classList.remove('js-hide');
		} else {
			this.DOM.section5.classList.add('js-hide');
		}
	}

	renderConditionalFields() {
		if (this.state.source === 'FREEFLY') {
			this.DOM.orderNumberField.classList.remove('js-hide');
		} else {
			this.DOM.orderNumberField.classList.add('js-hide');
		}

		if (this.state.source === 'DEALER') {
			this.DOM.dealerNameField.classList.remove('js-hide');
		} else {
			this.DOM.dealerNameField.classList.add('js-hide');
		}

		// if( this.state.family === "OTHER" ){
		//   this.DOM.productTextWrapper.classList.remove("js-hide")
		// }else{
		//   this.DOM.productTextWrapper.classList.add("js-hide")
		// }

		if (['DRONE', 'GIMBAL', 'CAMERA'].indexOf(this.state.family) > -1) {
			this.DOM.productSelectWrapper.classList.remove('js-hide');
		} else {
			this.DOM.productSelectWrapper.classList.add('js-hide');
		}

		const foundProduct = this.state.product
			? this.serialNumberPanels.find((el) => el.id === this.state.product)
			: null;
		if (foundProduct) {
			this.DOM.serialNumberRow.classList.remove('js-hide');
		} else {
			this.DOM.serialNumberRow.classList.add('js-hide');
		}

		if (this.state.product === 'OTHER_ACCESSORY') {
			this.DOM.otherProductRow.classList.remove('js-hide');
		} else {
			this.DOM.otherProductRow.classList.add('js-hide');
		}

		if (this.state.estimate) {
			this.DOM.preauthorizeRow.classList.add('js-hide');
		} else {
			this.DOM.preauthorizeRow.classList.remove('js-hide');
		}
	}

	renderErrors() {
		if (this.state.emailIsValid === false) {
			this.DOM.email.classList.add('js-error');
		} else {
			this.DOM.email.classList.remove('js-error');
		}

		if (this.state.serialIsValid === false) {
			this.DOM.serialNumber.classList.add('js-error');
		} else {
			this.DOM.serialNumber.classList.remove('js-error');
		}

		if (this.state.purchaseDateIsValid === false) {
			this.DOM.purchaseDate.classList.add('js-error');
		} else {
			this.DOM.purchaseDate.classList.remove('js-error');
		}
	}

	renderFooter() {
		if (this.state.valid) {
			this.DOM.advanceButton.disabled = false;
		} else {
			this.DOM.advanceButton.disabled = true;
		}
		if (this.state.valid && !this.state.working && !this.state.success) {
			this.DOM.submitButton.disabled = false;
		} else {
			this.DOM.submitButton.disabled = true;
		}
		if (this.state.step === 5) {
			this.DOM.advanceButton.classList.add('js-hide');
			this.DOM.submitButton.classList.remove('js-hide');
		} else {
			this.DOM.advanceButton.classList.remove('js-hide');
			this.DOM.submitButton.classList.add('js-hide');
		}
		if (this.state.working) {
			this.DOM.spinner.classList.remove('js-hide');
		} else {
			this.DOM.spinner.classList.add('js-hide');
		}
	}

	validate() {
		let valid = true;
		if (this.state.step >= 1) {
			const step1Valid = this.validateStep1();
			if (!step1Valid) valid = false;
		}
		if (this.state.step >= 2) {
			const step2Valid = this.validateStep2();
			if (!step2Valid) valid = false;
		}
		if (this.state.step >= 3) {
			const step3Valid = this.validateStep3();
			if (!step3Valid) valid = false;
		}
		if (this.state.step >= 4) {
			const step4Valid = this.validateStep4();
			if (!step4Valid) valid = false;
		}
		if (this.state.step >= 5) {
			const step5Valid = this.validateStep5();
			if (!step5Valid) valid = false;
		}

		this.state.valid = valid;
		return valid;
	}

	validateEmail(email) {
		const s = typeof email === 'string' ? email.trim() : '';
		if (s.toLowerCase() === BLOCKED_SERVICE_REQUEST_EMAIL) return false;
		if (s.length > 100) return false;
		const split = s.split('@');
		if (
			split.length !== 2 || //there is no @ or more than one
			split[0] === '' ||
			split[1] === ''
		) {
			return false;
		}
		const domainSplit = split[1].split('.');
		const suffix = domainSplit[domainSplit.length - 1];
		if (
			domainSplit.length === 1 || //there is no .
			suffix.length < 2
		) {
			return false;
		}
		return true;
	}

	validateSerial(serial) {
		if (!serial) return true;
		if (serial.match(/[^0-9a-z\-_]/i)) {
			return false;
		}
		return true;
	}

	/**
	 * Purchase date from <input type="date"> must be YYYY-MM-DD with a real calendar date,
	 * year 2010–current (company founding; rejects leading-zero typos like 0226-01-07), and not in the future.
	 */
	validatePurchaseDate(raw) {
		const s = typeof raw === 'string' ? raw.trim() : '';
		if (!s) return false;
		const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
		if (!m) return false;
		const y = parseInt(m[1], 10);
		const month = parseInt(m[2], 10);
		const day = parseInt(m[3], 10);
		const maxYear = new Date().getFullYear();
		if (y < 2010 || y > maxYear) return false;
		const d = new Date(y, month - 1, day);
		if (d.getFullYear() !== y || d.getMonth() !== month - 1 || d.getDate() !== day) return false;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		d.setHours(0, 0, 0, 0);
		if (d > today) return false;
		return true;
	}

	validateStep1() {
		if (!this.DOM.fName.value) return false;
		if (!this.DOM.lName.value) return false;
		const email = this.DOM.email.value;
		if (!email) {
			this.state.emailIsValid = true; //don't bother user until they have entered a value
			return false;
		}
		const emailIsValid = this.validateEmail(email);
		if (!emailIsValid) {
			this.state.emailIsValid = false;
			return false;
		} else {
			this.state.emailIsValid = true;
		}
		return true;
	}

	validateStep2() {
		if (!this.DOM.source.value) return false;
		const pd = this.DOM.purchaseDate.value;
		if (!pd) {
			this.state.purchaseDateIsValid = null;
			return false;
		}
		if (!this.validatePurchaseDate(pd)) {
			this.state.purchaseDateIsValid = false;
			return false;
		}
		this.state.purchaseDateIsValid = true;
		return true;
	}

	validateStep3() {
		if (!this.DOM.productFamily.value) return false;
		// if( !this.DOM.serialNumber.value ) return false
		const serial = this.DOM.serialNumber.value;
		const serialIsValid = this.validateSerial(serial);
		if (!serialIsValid) {
			this.state.serialIsValid = false;
			return false;
		} else {
			this.state.serialIsValid = true;
		}
		// if( this.state.product === "OTHER_ACCESSORY" && !this.DOM.otherProduct.value ){
		//   return false
		// }

		if (
			['DRONE', 'GIMBAL', 'CAMERA', 'POWER SYSTEMS'].indexOf(this.DOM.productFamily.value) > -1 &&
			!this.DOM.productSelect.value
		)
			return false;
		// if(
		//   this.DOM.productFamily.value === "OTHER"
		//   && !this.DOM.productText.value
		// ) return false
		return true;
	}

	validateStep4() {
		if (!this.DOM.nature.value) return false;
		if (!this.DOM.description.value) return false;
		if (!this.DOM.attempt.value) return false;
		return true;
	}
	validateStep5() {
		return true;
	}

	sourceInput(e) {
		this.state.source = e.currentTarget.value;
		this.render();
	}

	productSelectInput(e) {
		const product = e.currentTarget.value;
		if (this.state.product === product) return;
		this.state.product = product;
		this.DOM.serialNumber.value = '';
		this.DOM.otherProduct.value = '';
		this.validate();
		this.render();
	}

	familyInput(e) {
		const family = e.currentTarget.value;
		if (this.state.family === family) return;
		this.state.family = family;
		this.state.product = null;
		const options = this.productOptions[family] || [];
		const optionsHTML = `
      <option value="" selected></option>
      ${options.map((el) => `<option value="${el.value}">${el.label}</option>`).join('')}
    `;
		this.DOM.productSelect.innerHTML = optionsHTML;
		this.DOM.productSelect.selectedIndex = 0;
		this.DOM.serialNumber.value = '';
		this.DOM.otherProduct.value = '';
		this.render();
	}

	estimateChange(e) {
		this.state.estimate = e.currentTarget.value === 'yes';
		this.render();
	}

	serialInfoTriggerClick() {
		const option = this.productOptions[this.state.family].find(
			(el) => el.value === this.state.product
		);
		const data = this.serialNumberPanels.find((el) => el.id === this.state.product);
		if (!data) return;
		const html = `<div class="ff-serviceRequestInfoPanel">
      <div class="header">
        <p>${option ? option.label : ''}</p>
      </div>
      <div class="body">
        <p>${data.text}</p>
        <div class="image">
          <img src="${templateDirectoryUri}/dist/images/service/${data.image}" />
        </div>
      </div>
    </div>`;
		openInlineHtml(html, {
			skin: 'ff-serviceRequestModal',
			width: 'auto',
			height: 'auto',
			draggable: false,
			touchNavigation: false,
		});
	}

	initUpload() {
		registerPlugin(FilePondPluginFileValidateSize);
		registerPlugin(FilePondPluginFileValidateType);

		const pondConfig = {
			credits: false as const,
			maxFiles: 10,
			acceptedFileTypes: [
				'image/*',
				'application/pdf',
				'application/csv',
				'application/octet-stream',
			],
			maxFileSize: '5MB',
			allowMultiple: true,
			server: {
				process: {
					url: ajaxurl,
					onload: (response) => {
						response = JSON.parse(response);
						// console.log(response);
						return response.key;
					},
					ondata: (formData) => {
						formData.append('action', 'service_s3_upload');
						formData.append('id', this.submissionId);
						return formData;
					},
				},
			},
		};

		this.orderPond = create(this.DOM.orderFiles, pondConfig);
		this.issuePond = create(this.DOM.issueFiles, pondConfig);
	}

	getSuccessText(ra) {
		return `
<p>We received your service request. Your RA reference number is <code>${ra}</code>.</p>
<p>Shortly, you’ll get an email with instructions on how to package your items for shipping.</p>
${['ASTRO', 'ALTA_X', 'SONY_MAPPING_PAYLOAD', 'WIRIS_PRO_PAYLOAD'].indexOf(this.state.product) > -1 ? `<p><strong>Important:</strong> For drone service, you must include the controller for testing purposes.</p>` : ''}
${['ASTRO', 'SONY_MAPPING_PAYLOAD', 'WIRIS_PRO_PAYLOAD'].indexOf(this.state.product) > -1 ? `<p><strong>Important:</strong> For Astro service, you must allow the aircraft for transfer in the Auterion Suite before shipping the unit.</p>` : ''}
<p>Thanks for choosing Freefly!</p>
    `;
	}

	showSuccessMessage(ra) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'success');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Success!</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      ${this.getSuccessText(ra)}
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	showErrorMessage(message) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'error');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Error</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      <p>Uh oh. There was an issue with the form submission:</p>
      ${message ? `<pre>${message}</pre>` : ''}
      <p><a href="mailto:support@freeflysystems.com">Email support</a> for help with your service request.</p>
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	productOptions = {
		DRONE: [
			{ value: 'ASTRO', label: 'Astro (any version)' },
			{ value: 'ALTA_X', label: 'Alta X (any version)' },
			{ value: 'A7R_SONY_MAPPING_PAYLOAD', label: 'A7R Sony Mapping Payload' },
			{ value: 'LR1_SONY_MAPPING_PAYLOAD', label: 'LR1 Sony Mapping Payload' },
			{ value: 'OGI_PAYLOAD', label: 'OGI Payload' },
			{ value: 'FLUX', label: 'Flux LiDAR' },
			{ value: 'WIRIS_PRO_PAYLOAD', label: 'Wiris Pro Payload' },
			{ value: 'PILOT_PRO', label: 'Pilot Pro Controller (any version)' },
			{ value: 'UXV_CONTROLLER', label: 'UXV Controller (any version)' },
			{ value: 'SL8_BATTERY', label: 'SL8-Air Battery' },
		],
		GIMBAL: [
			{ value: 'MOVI_PRO', label: 'Mōvi Pro' },
			{ value: 'MOVI_XL', label: 'Mōvi XL' },
			{ value: 'MOVI_CARBON', label: 'Mōvi Carbon' },
			{ value: 'MOVI_CONTROLLER', label: 'Mōvi Controller' },
			{ value: 'MIMIC', label: 'Mimic' },
			{ value: 'WHEELS', label: 'Mōvi Wheels' },
			{ value: 'FOG', label: 'Fiber Optic Gyro (F.O.G.)' },
			{ value: 'SL4_BATTERY', label: 'SL4-Ground Battery' },
			{ value: 'OTHER_ACCESSORY', label: 'Other Gimbal Accessories' },
		],
		CAMERA: [
			{ value: 'WAVE', label: 'Wave' },
			{ value: 'EMBER', label: 'Ember S5K' },
			{ value: 'EMBER_2_5', label: 'Ember S2.5K' },
			{ value: 'EMBER_EXTERNAL_SSD', label: 'Ember External SSD' },
			{ value: 'ACTIVE_EF_MOUNT', label: 'Active EF Mount' },
			{ value: 'OTHER_ACCESSORY', label: 'Other Camera Accessories' },
		],
	};

	serialNumberPanels = [
		{
			id: 'MOVI_PRO',
			image: 'sn-movi-pro.jpg ',
			text: 'Your serial number is engraved on the underside of the horizontal, aluminum roll beam and starts with "MPRO," with an "o" as in "pro", not a zero.',
		},
		{
			id: 'MOVI_CARBON',
			image: 'sn-movi-carbon.jpg ',
			text: 'Your serial number is engraved on the underside of the horizontal, aluminum roll beam and starts with "MPRO," with an "o" as in "pro", not a zero.',
		},
		{
			id: 'WAVE',
			image: 'sn-wave.jpg',
			text: 'Your serial number starts with "W" followed by 6 numbers and can be found on the bottom of the camera.',
		},
		{
			id: 'EMBER',
			image: 'sn-ember.jpg',
			text: 'Your serial number starts with "E" followed by 6 numbers and can be found on the bottom of the camera.',
		},
		{
			id: 'A7R_SONY_MAPPING_PAYLOAD',
			image: 'sn-sony-mapping-payload.jpg',
			text: 'Your serial number starts with "132" followed by 5 numbers and can be found on the dovetail where the payload installs to the drone.',
		},
		{
			id: 'LR1_SONY_MAPPING_PAYLOAD',
			image: 'sn-sony-mapping-payload.jpg',
			text: 'Your serial number starts with "169" followed by 5 numbers and can be found on the dovetail where the payload installs to the drone.',
		},
		{
			id: 'OGI_PAYLOAD',
			image: 'sn-sony-mapping-payload.jpg',
			text: 'Your serial number starts with "182" followed by 5 numbers and can be found on the dovetail where the payload installs to the drone.',
		},
		{
			id: 'WIRIS_PRO_PAYLOAD',
			image: 'sn-sony-mapping-payload.jpg',
			text: 'Your serial number starts with "147" followed by 5 numbers and can be found on the dovetail where the payload installs to the drone.',
		},
		{
			id: 'PILOT_PRO',
			image: 'sn-pilot-pro.jpg',
			text: 'Your serial number starts with "158" followed by 5 numbers.',
		},

		{
			id: 'MOVI_XL',
			image: 'sn-movi-xl.jpg',
			text: 'Your serial number is engraved on the power distribution box on the back of the Mōvi XL, and starts with "MXL."',
		},
		{
			id: 'ALTA_X',
			image: 'sn-alta-x.jpg',
			text: 'Your serial number is on a sticker on the underside of the Alta X air frame.',
		},
		{
			id: 'MOVI_CONTROLLER',
			image: 'sn-movi-controller.jpg',
			text: 'Your serial number is engraved on the input port plate on the top left shoulder of the controller, near where the power cable plugs in. It will be 4-6 alpha-numeric characters, potentially starting with "MC."',
		},
		{
			id: 'FOG',
			image: 'sn-fog.jpg',
			text: 'Your serial number is 8 characters, alphanumeric and printed on the sticker on the top of the FOG.',
		},
		{
			id: 'WHEELS',
			image: 'sn-movi-wheels.jpg',
			text: 'Your serial number starts with "WHL" followed by 4 numbers and is engraved on each Wheel module behind the mounting bracket.',
		},
		{
			id: 'ASTRO',
			image: 'sn-astro.jpg',
			text: 'Your serial number starts with "109" or "997" followed by 5 numbers and is affixed on the underside of the chassis.',
		},
		{
			id: 'SL8_BATTERY',
			image: 'sn-sl8-battery.jpg',
			text: 'Your serial number starts with "649" followed by 5 numbers and is on the label on the back of the battery, at the bottom right.',
		},
		{
			id: 'SL4_BATTERY',
			image: 'sn-sl4-battery.jpg',
			text: 'Your serial number starts with "653" followed by 5 numbers and is on the label on the back of the battery, at the bottom right.',
		},
		{
			id: 'FLUX',
			image: 'sn-sony-mapping-payload.jpg',
			text: 'Your serial number is 8 alphanumeric characters and can be found where the payload installs to the drone.',
		},
	];

	scrollToStep() {
		const stepEl = this.DOM[`section${this.state.step - 1}`];
		if (!stepEl) return;
		const height = Math.min(300, stepEl.getBoundingClientRect().height);
		window.scrollTo({ top: window.scrollY + height, behavior: 'smooth' });
	}
}

if (document.querySelector('.js-serviceRequestForm')) {
	new FF_serviceRequest();
}

// {
//   firstName: string,
//   lastName: string,
//   phone?: string,
//   email: string,
//   company?: string

//   source: "FREEFLY" | "DEALER" | "OTHER"
//   dealerName?: string
//   orderNumber?: string
//   purchaseDate: string

//   productFamily: string
//   product: string
//   serial: string

//   nature: string
//   warranty?: boolean
//   description: string
//   attempt: string

//   requestEstimate: boolean
//   preauthorize?: number

//   droneArmor?: text
// }
